import { List } from '@mui/material'
import { Fragment } from 'react'
import { Divider } from 'utoniq-ui'

import {
  Maybe,
  OrganizationNotificationConnectionFragment,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { ScrollFetcher } from '@/components/organisms/functionals'
import {
  NotificationBox,
  NotificationBoxSkeleton,
} from '@/components/organisms/notification'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { individualDisplayResponsive } from '@/core/individual'

export * from './fragments'

export type State = {
  loading: boolean
  refetching: boolean
  hasNextPage: boolean
  notifications: Maybe<OrganizationNotificationConnectionFragment>
}

type NotificationListCellProps = Pick<
  State,
  'loading' | 'hasNextPage' | 'notifications'
> & {
  onFetchMore: Action['onFetchMore']
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFetchMore: () => Promise<void>
}

export const OfficialInformationTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
}) => {
  const { t } = useTranslation('officialInformation')
  const { routerNames } = useRouter()
  const displayResponsive = individualDisplayResponsive()
  return (
    <BaseLayout
      header={{
        back: true,
        title: t('title'),
        ...(displayResponsive
          ? {
              responsive: true,
              maxWidth: 1120,
            }
          : {}),
      }}
      maxWidth={displayResponsive ? 1120 : undefined}
    >
      <Head title={routerNames.OfficialInformation} />
      <NotificationListCell
        loading={state.loading || state.refetching}
        notifications={state.notifications}
        hasNextPage={state.hasNextPage}
        onFetchMore={onFetchMore}
      />
    </BaseLayout>
  )
}

const NotificationListCell: Cell<NotificationListCellProps> = ({
  loading,
  hasNextPage,
  notifications,
  onFetchMore,
}) => {
  const { t } = useTranslation('officialInformation')
  return (
    <CellFrame>
      <ScrollFetcher
        nodes={notifications?.edges.map(({ node }, index) => (
          <Fragment key={node.id}>
            <NotificationBox notification={node} />
            {notifications?.edges.length - 1 > index && (
              <Divider sx={{ mx: 5 }} />
            )}
          </Fragment>
        ))}
        loading={loading}
        hasNextPage={hasNextPage}
        onFetchMore={onFetchMore}
        componentProps={{ spacing: 0 }}
        component={List}
        emptyMessage={t('notExist')}
        loadingComponent={
          <List>
            {[1, 2, 3, 4, 5].map(index => (
              <NotificationBoxSkeleton key={index} />
            ))}
          </List>
        }
      />
    </CellFrame>
  )
}
